import { AxiosError } from "axios";
import { useRef, useState, useEffect, useMemo } from "react";
import { Select } from "flowbite-react";
import { Spinner } from "../../components/atoms/Spinner";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import * as Joi from "joi";
import { joiResolver } from "@hookform/resolvers/joi";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { TFunction } from "i18next";
import { getLanguageCode } from "../../utils/languageUtility";
import moment from "moment";
import BrokenLinkError from "../BrokenLinkError";
import CountryOptions from "../../components/public/CountryOptions";
import { HiCheck, HiUser, HiX } from "react-icons/hi";
import BsInputPassword from "../../components/public/BsInputPassword";
import { CustomInput } from "../../components/atoms/CustomInput";
import { CustomButton } from "../../components/atoms/CustomButton";
import { ModalShell } from "../../components/atoms/ModalShell";
import { useGetEntityBasicInfo } from "../../hooks/entity";
import { useCdvAvatar } from "../../hooks/cdn";
import { useUserRegisterByLink } from "../../hooks/users";
import LanguageSelector from "../../components/atoms/LanguageSelector";
import { CustomPhoneNumber } from "../../components/atoms/CustomPhoneNumber";
import { useLanguageDetection } from "../../hooks/useLanguageDetection";
import { FieldLabel } from "../../components/atoms/FieldLabel";
import {
  refusalMessageKey,
  refusedFields,
} from "../../utils/refusalMessageKey";
import { SIGN_UP_REFUSALS } from "../../components/pages/customRegister/registrationRefusals";
import {
  KNOWN_PASSWORD_LEVELS,
  meetsPasswordRules,
  usePasswordRules,
} from "../../hooks/usePasswordResetContext";

const UNEXPLAINED_REFUSAL = "customRegistration.page.toast.registrationFailed";

type RegisterType = {
  user_first_name: string | null;
  user_last_name: string | null;
  user_birth_date: string | null;
  user_country_code: string | null;
  user_user_name: string | null;
  user_email: string;
  user_password: string;
  user_confirm_password: string;
  user_role: string;
  user_type: string;
  user_avatar: string | null;
  user_phone: string | null;
};

/** UMD's registration limits for the user name, first name and last name. */
const NAME_LENGTH = { min: 2, max: 30 };

const buildSchema = (t: TFunction) => {
  const text = (key: string, values: Record<string, number> = {}) =>
    String(t(`register.form.${key}`, values));
  const length = (key: string, min: number, max: number) => ({
    "string.base": text("checkField"),
    "string.min": text(key, { min, max }),
    "string.max": text(key, { min, max }),
  });

  return Joi.object({
    user_avatar: Joi.string()
      .trim()
      .uri()
      .optional()
      .allow("", null)
      .messages({
        "string.base": text("avatarUploadFailed"),
        "string.uri": text("avatarUploadFailed"),
      }),
    user_email: Joi.string()
      .trim()
      .required()
      .messages({
        "string.base": text("emailRequired"),
        "string.empty": text("emailRequired"),
        "any.required": text("emailRequired"),
      }),
    user_user_name: Joi.string()
      .trim()
      .min(NAME_LENGTH.min)
      .max(NAME_LENGTH.max)
      .optional()
      .allow(null, "")
      .messages(length("userNameLength", NAME_LENGTH.min, NAME_LENGTH.max)),
    user_first_name: Joi.string()
      .trim()
      .min(NAME_LENGTH.min)
      .max(NAME_LENGTH.max)
      .optional()
      .allow(null, "")
      .messages(length("firstNameLength", NAME_LENGTH.min, NAME_LENGTH.max)),
    user_last_name: Joi.string()
      .trim()
      .min(NAME_LENGTH.min)
      .max(NAME_LENGTH.max)
      .optional()
      .allow(null, "")
      .messages(length("lastNameLength", NAME_LENGTH.min, NAME_LENGTH.max)),
    user_birth_date: Joi.date()
      .optional()
      .allow(null, "")
      .messages({ "date.base": text("birthDateInvalid") }),
    user_country_code: Joi.string()
      .trim()
      .optional()
      .allow(null, "")
      .messages({ "string.base": text("checkField") }),
    user_password: Joi.string()
      .trim()
      .required()
      .messages({
        "string.base": text("passwordRequired"),
        "string.empty": text("passwordRequired"),
        "any.required": text("passwordRequired"),
      }),
    user_phone: Joi.string()
      .trim()
      .max(15)
      .optional()
      .allow(null, "")
      .messages({
        "string.base": text("checkField"),
        "string.max": text("phoneMaxLength"),
      }),
  }).messages({ "object.unknown": text("checkField") });
};

const RegisterViaLink = () => {
  const { entity_identification } = useParams();
  const { t, i18n } = useTranslation("clientView");
  const schema = useMemo(() => buildSchema(t), [t]);
  const { passwordLevel, passwordRules, acceptedLevels } = usePasswordRules(
    entity_identification ?? null
  );
  const passwordRequirement =
    passwordLevel && KNOWN_PASSWORD_LEVELS.has(passwordLevel)
      ? (t(`passwordLevels.${passwordLevel}`) as string)
      : undefined;
  const followsPasswordRules = (password?: string) =>
    meetsPasswordRules(
      password ?? "",
      { level: passwordLevel ?? "", rules: passwordRules },
      acceptedLevels
    );
  const isRTL = getLanguageCode(i18n.language) === "ar";

  const inputFileRef = useRef<any>(null);

  const [serviceDetail, setServiceDetail] = useState<{ [key: string]: any }>(
    {}
  );

  const [successRegModal, setSuccessRegModal] = useState(false);
  const [errorRegModal, setErrorRegModal] = useState(false);
  const [apiError, setApiError] = useState<{ [key: string]: string }>({});
  const [isServiceDeactive, setIsServiceDeactive] = useState(false);
  const [toggleInputDateType, setToggleInputDateType] = useState<
    "text" | "date"
  >("text");

  const { isLoading, isSuccess, isError, refetch } = useGetEntityBasicInfo(
    entity_identification as string,
    {
      onSuccess: (data) => {
        setServiceDetail(data);
        setErrorRegModal(false);
      },
      onError: (err: AxiosError<any, any>) => {
        if (err.response?.data.code === "RSUSKO010") setIsServiceDeactive(true);
        else setErrorRegModal(true);
      },
    }
  );

  const { mutate: cdnAvatar, isLoading: isUploading } = useCdvAvatar();

  const { mutate: registerBylink, isLoading: isRegistering } =
    useUserRegisterByLink();

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    getValues,
    watch,
    control,
  } = useForm<RegisterType>({
    resolver: joiResolver(schema),
    defaultValues: {
      user_avatar: "",
      user_birth_date: "",
      user_country_code: "",
      user_email: "",
      user_first_name: "",
      user_last_name: "",
      user_password: "",
      user_phone: "",
      user_user_name: "",
    },
  });

  useEffect(() => {
    const ownerCountryCode = serviceDetail?.owner?.user_country_code;
    if (ownerCountryCode && !getValues("user_country_code")) {
      setValue("user_country_code", ownerCountryCode);
    }
  }, [serviceDetail?.owner?.user_country_code, setValue, getValues]);

  const processDataForLanguage = useMemo(() => {
    if (!serviceDetail?.owner?.user_country_code) return null;

    return {
      entity_data: {
        owner_data: {
          user_country_code: serviceDetail.owner.user_country_code,
        },
      },
    };
  }, [serviceDetail?.owner?.user_country_code]);

  useLanguageDetection(processDataForLanguage);

  const handleTriggerFileInput = () => {
    let inputFileElement = document.getElementById("avatarInput");

    inputFileElement?.click();
  };

  const handleUploadAvatar = (files: FileList | null) => {
    if (files && files.length > 0) {
      if (files[0].size > 3 * 1048576)
        return toast.error(t("register.form.fileTooLarge"));

      cdnAvatar(
        {
          entity_identification: entity_identification,
          data: { image: files[0] } as any,
        },
        {
          onSuccess: (res: any) => {
            if (res?.data?.element && res.data.element[0]?.cdnPath) {
              setValue("user_avatar", res.data.element[0].cdnPath);
            } else {
              toast.error(t("register.form.avatarUploadFailed"));
              console.error("Unexpected response format:", res);
            }
          },
          onError: (err: any) => {
            toast.error(t("register.form.avatarUploadFailed"));
            console.error("Upload error:", err);
          },
        }
      );
    }
  };

  const handleDeleteAvatarFile = () => {
    setValue("user_avatar", "");
    inputFileRef.current.value = null;
  };

  const onSubmit = (data: any) => {
    setApiError({});

    let finalPayload = {
      ...data,
      user_confirm_password: data.user_password,
      user_birth_date: moment(data.user_birth_date)
        .format("YYYY-MM-DD")
        .toString(),
      user_role: "default",
      user_type: "default",
    };

    Object.keys(finalPayload).forEach((key) => {
      if (
        finalPayload[key] === "" ||
        finalPayload[key] === null ||
        finalPayload[key] === undefined
      )
        delete finalPayload[key];

      if (key === "user_birth_date" && finalPayload[key] === "Invalid date")
        delete finalPayload[key];
    });

    registerBylink(
      {
        entity_identification: entity_identification,
        data: finalPayload,
      },
      {
        onSuccess: (data) => {
          setSuccessRegModal(true);
        },
        onError: (err) => {
          const fields = refusedFields(err);
          if (fields.length > 0) {
            const passwordRefused =
              !!passwordRequirement &&
              !followsPasswordRules(getValues("user_password"));
            setApiError(
              Object.fromEntries(
                fields.map((field) => [
                  field,
                  field === "user_password" && passwordRefused
                    ? (passwordRequirement as string)
                    : (t("register.form.checkField") as string),
                ])
              )
            );
            return;
          }
          const message = refusalMessageKey(
            err,
            SIGN_UP_REFUSALS,
            UNEXPLAINED_REFUSAL
          );
          if (
            message === UNEXPLAINED_REFUSAL &&
            !followsPasswordRules(getValues("user_password"))
          ) {
            setApiError({
              user_password:
                passwordRequirement ??
                (t("register.form.checkField") as string),
            });
            return;
          }
          toast.error(t(message));
        },
      }
    );
  };

  let content;

  if (isLoading)
    content = (
      <div className="h-28 flex items-center justify-center">
        <Spinner />
      </div>
    );

  if (isError && isServiceDeactive) content = <BrokenLinkError />;

  if (isError && !isServiceDeactive) content = null;

  if (isSuccess)
    content = (
      <div className="w-full md:w-[792px] mx-auto self-center px-6 pt-6 pb-10 bg-white rounded-lg shadow border border-gray-300 flex-col justify-start items-center gap-6 inline-flex relative">
        <div className="absolute top-4 right-4 z-[9999] [&_button]:bg-gray-200 [&_button]:hover:bg-gray-300 [&_button]:text-gray-700">
          <LanguageSelector variant="compact" />
        </div>

        <div className="flex flex-col items-center justify-center gap-4">
          {serviceDetail?.entity_informations?.entity_logo && (
            <img
              src={serviceDetail?.entity_informations?.entity_logo}
              alt={serviceDetail.entity_informations.entity_name}
              className="w-16 h-16 relative bg-gray-100 rounded-full shrink-0"
            />
          )}

          {serviceDetail?.entity_informations?.entity_name && (
            <span className="text-gray-700 text-lg font-bold font-figtree leading-7">
              {serviceDetail.entity_informations.entity_name}
            </span>
          )}

          <h3 className="text-center text-gray-700 text-2xl font-bold font-figtree leading-loose">
            {t("register.form.title")}
          </h3>
        </div>

        <div className="flex flex-col justify-start items-center gap-6">
          <div className="w-16 h-16 relative bg-gray-100 rounded-full overflow-hidden">
            {getValues("user_avatar") ? (
              <img
                className="w-16 h-16 object-cover rounded-full"
                src={getValues("user_avatar") as string}
                alt={t("register.form.avatar") as string}
              />
            ) : (
              <HiUser size={64} className="text-gray-300 mt-2" />
            )}
          </div>
          <div className="">
            <div className="flex justify-center items-center gap-2">
              <input
                type={"file"}
                hidden
                id="avatarInput"
                accept=".jpg,.png,.jpeg"
                onChange={(e) => handleUploadAvatar(e.target.files)}
                ref={inputFileRef}
              />
              <CustomButton
                onClick={handleTriggerFileInput}
                disabled={isUploading}
                isLoading={isUploading}
                title={
                  watch("user_avatar") === "" || watch("user_avatar") === null
                    ? t("register.form.addProfilePicture")
                    : t("register.form.replacePicture")
                }
              />

              {watch("user_avatar") && (
                <CustomButton
                  title={t("register.form.delete")}
                  onClick={handleDeleteAvatarFile}
                />
              )}
            </div>
            {(apiError?.user_avatar || !!errors.user_avatar) && (
              <p className="text-red-700">
                {apiError?.user_avatar || errors?.user_avatar?.message}
              </p>
            )}
          </div>
        </div>

        <form
          onSubmit={handleSubmit(onSubmit)}
          className="w-full flex flex-col justify-start items-center gap-6"
        >
          <input hidden {...register("user_avatar")} />

          <div className="w-full">
            <CustomInput
              name="user_email"
              control={control}
              error={errors.user_email || apiError?.user_email}
              helperText={errors.user_email?.message || apiError?.user_email}
              label={t("register.form.email") as string}
              type="email"
              placeholder={t("register.form.emailPlaceholder") as string}
              grayBackground
              autoComplete="email"
            />
          </div>

          <div className="w-full relative" dir={isRTL ? "rtl" : "ltr"}>
            <BsInputPassword
              rhfControls={register("user_password")}
              errorMessage={
                errors.user_password?.message || apiError?.user_password
              }
              isInvalid={!!errors.user_password || !!apiError?.user_password}
              label={t("register.form.password") as string}
              placeholder={t("register.form.passwordPlaceholder") as string}
              rest={{ autoComplete: "new-password" }}
            />
            {passwordRequirement &&
              !errors.user_password &&
              !apiError?.user_password && (
                <p dir="auto" className="mt-1 text-xs text-gray-500">
                  {passwordRequirement}
                </p>
              )}
          </div>

          <div className="w-full flex flex-col md:flex-row justify-start items-center gap-6">
            <div className="w-full">
              <CustomInput
                name="user_first_name"
                control={control}
                error={errors.user_first_name || apiError?.user_first_name}
                helperText={
                  errors.user_first_name?.message || apiError?.user_first_name
                }
                label={t("register.form.firstName") as string}
                placeholder={t("register.form.firstNamePlaceholder") as string}
                grayBackground
                autoComplete="given-name"
              />
            </div>
            <div className="w-full">
              <CustomInput
                name="user_last_name"
                control={control}
                error={errors.user_last_name || apiError?.user_last_name}
                helperText={
                  errors.user_last_name?.message || apiError?.user_last_name
                }
                label={t("register.form.lastName") as string}
                placeholder={t("register.form.lastNamePlaceholder") as string}
                grayBackground
                autoComplete="family-name"
              />
            </div>
          </div>

          <div className="w-full flex flex-col md:flex-row justify-start items-center gap-6">
            <div className="w-full">
              <CustomInput
                name="user_birth_date"
                control={control}
                error={errors.user_birth_date || apiError?.user_birth_date}
                helperText={
                  errors.user_birth_date?.message || apiError?.user_birth_date
                }
                label={t("register.form.birthDate") as string}
                grayBackground
                type={toggleInputDateType}
                placeholder={t("register.form.birthDatePlaceholder") as string}
                onFocus={() => setToggleInputDateType("date")}
                max={moment().format("YYYY-MM-DD")}
                onBlur={(e) => {
                  setToggleInputDateType("text");
                }}
                autoComplete="bday"
              />
            </div>

            <div className="w-full" dir={isRTL ? "rtl" : "ltr"}>
              <FieldLabel
                htmlFor="user_country_code"
                className="text-sm font-medium text-gray-700"
                style={{ textAlign: isRTL ? "right" : "left" }}
              >
                {t("register.form.region")}
              </FieldLabel>
              <Select
                translate="no"
                id="user_country_code"
                color={
                  !!errors.user_country_code || !!apiError?.user_country_code
                    ? "failure"
                    : undefined
                }
                {...register("user_country_code")}
                helperText={
                  errors.user_country_code?.message ||
                  apiError?.user_country_code
                }
                autoComplete="country"
              >
                <option value="">
                  {t("register.form.regionPlaceholder") as string}
                </option>
                <CountryOptions />
              </Select>
            </div>
          </div>

          <div className="w-full">
            <CustomPhoneNumber
              defaultCountryCode={watch("user_country_code") || undefined}
              name="user_phone"
              control={control}
              error={errors.user_phone || apiError?.user_phone}
              helperText={
                errors.user_phone?.type === "string.max"
                  ? (t("register.form.phoneMaxLength") as string)
                  : errors.user_phone?.message || apiError?.user_phone
              }
              label={t("register.form.phone") as string}
              placeholder={t("register.form.phonePlaceholder") as string}
              maxLength={13}
            />
          </div>

          <div className="w-full">
            <CustomInput
              name="user_user_name"
              control={control}
              error={errors.user_user_name || apiError?.user_user_name}
              helperText={
                errors.user_user_name?.message || apiError?.user_user_name
              }
              label={t("register.form.userName") as string}
              grayBackground
              placeholder={t("register.form.userNamePlaceholder") as string}
              autoComplete="username"
            />
          </div>

          <div className="w-full">
            <CustomButton
              type="submit"
              className="w-full"
              disabled={isRegistering}
              isLoading={isRegistering}
              title={t("register.form.submit")}
            />
          </div>
        </form>
      </div>
    );

  return (
    <div
      className="w-full min-h-screen py-6 px-2 bg-gray-500 bg-opacity-75 flex justify-center items-start registerViaLink"
      translate="no"
    >
      {content}

      <ModalShell
        show={successRegModal}
        size="md"
        onClose={() => setSuccessRegModal(false)}
        bareCloseButton
      >
        <div className="flex flex-col items-center justify-start text-center">
          <HiCheck size={24} className="mx-auto text-green-400" />
          <h4 className="mt-2 text-lg font-semibold text-gray-900">
            {t("register.form.success")}
          </h4>
          <p className="mt-0.5 text-sm text-gray-500">
            {t("register.form.successMessage")}
          </p>
        </div>
      </ModalShell>

      <ModalShell
        show={errorRegModal}
        size="md"
        onClose={() => {}}
        footer={
          <>
            <a
              href={process.env.REACT_APP_HOST_URL}
              className="text-sm text-gray-500 no-underline"
            >
              {t("common.back")}
            </a>
            <CustomButton
              onClick={() => refetch()}
              variant="primary"
              title={t("register.form.tryAgain")}
            />
          </>
        }
      >
        <div className="flex flex-col items-center justify-start text-center">
          <HiX size={24} className="mx-auto text-red-400" />
          <h4 className="mt-2 text-lg font-semibold text-gray-900">
            {t("register.form.error")}
          </h4>
          <p className="mt-0.5 text-sm text-gray-500">
            {t("register.form.errorMessage")}
          </p>
        </div>
      </ModalShell>
    </div>
  );
};

export default RegisterViaLink;
